import React, { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { IMAGE_LIMITS, resizeImage } from '../utils/imageResize';

export const BACKGROUND_PAGES = [
  { key: 'VIEWER_HOME', label: 'Viewer desk' },
  { key: 'SCAN', label: 'Scan / print badge page' },
  { key: 'ON_SPOT', label: 'On-spot registration' },
];

/** One upload can be applied to several pages; each page can also get its own image. */
const PageBackgroundsEditor = ({ value, onChange }) => {
  const backgrounds = value || {};
  const [pages, setPages] = useState(() => BACKGROUND_PAGES.map((p) => p.key));
  const [uploading, setUploading] = useState(false);
  const { showError } = useToast();

  const togglePage = (key) =>
    setPages((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const upload = async (file) => {
    if (!file || pages.length === 0) return;
    setUploading(true);
    try {
      const url = await ApiService.uploadFormSchemaAsset('page_background', await resizeImage(file, IMAGE_LIMITS.page_background));
      const next = { ...backgrounds };
      pages.forEach((key) => { next[key] = url; });
      onChange(next);
    } catch (err) {
      showError(err.message || 'Background upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = (key) => {
    const next = { ...backgrounds };
    delete next[key];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <Label>Page backgrounds</Label>
      <p className="text-xs text-gray-500">
        Pick the pages, then upload an image to apply it to all of them. The public registration page is not affected.
      </p>
      <div className="flex flex-wrap gap-4">
        {BACKGROUND_PAGES.map((page) => (
          <label key={page.key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={pages.includes(page.key)} onChange={() => togglePage(page.key)} />
            {page.label}
          </label>
        ))}
      </div>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="text-sm"
        disabled={uploading || pages.length === 0}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          upload(file);
        }}
      />
      {uploading && <p className="text-xs text-gray-500">Uploading…</p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {BACKGROUND_PAGES.map((page) => (
          <div key={page.key} className="rounded-lg border p-2 space-y-2">
            <p className="text-xs font-medium text-gray-700">{page.label}</p>
            {backgrounds[page.key] ? (
              <>
                <img src={backgrounds[page.key]} alt={`${page.label} background`} className="h-20 w-full rounded object-cover" />
                <Button type="button" variant="outline" size="sm" onClick={() => remove(page.key)}>Remove</Button>
              </>
            ) : (
              <p className="text-xs text-gray-400">No background</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PageBackgroundsEditor;
